import { Dictionary } from 'lodash';
import React, { useCallback, useState } from 'react';
import { useQueryClient } from 'react-query';
import { AccountStatus } from 'api/account';
import { BillingAPI } from 'api/billing';
import { User, UserStatus } from 'api/models';
import { UserAPI } from 'api/user';
import { confirm } from 'components/atoms/modal';
import { toastService } from 'components/molecules/toast';
import { MembersResponse } from 'data/members/api';
import { useAccount, useBillingFeatureFlag } from 'hooks/use-account';
import { useTranslate } from 'hooks/use-translation';
import { ACCOUNT_META_DATA_QUERY_KEY } from 'queries/account';
import {
  SUBSCRIPTION_KEY,
  useAvailableLicensesQuery,
  useForceSyncSubscriptionMutation,
  useSubscriptionQuery,
} from 'queries/billing';
import { ALL_MEMBERS_QUERY_KEY } from 'queries/members';
import { ALL_PARTNER_COMPANIES_KEY } from 'queries/partner';
import { fullName } from 'utils/full-name';

export enum ReactivateStatus {
  Reactivating = 'reactivating',
  Reactivated = 'reactivated',
  Failed = 'failed',
}

interface Result {
  deactivateUser: (user: User) => void;
  reactivateUser: (user: User) => void;
  beforeReactivateUser: (user: User) => void;
  userForReactivatingBillingFlow?: User;
  reactivateStatus: Dictionary<ReactivateStatus>;
  onBillingFlowClose: () => void;
}
export const useUpdateTeamMemberStatus = (): Result => {
  const translate = useTranslate();
  const { mutateAsync: forceSyncSubscription } =
    useForceSyncSubscriptionMutation();
  const { data: subscription } = useSubscriptionQuery();
  const queryClient = useQueryClient();
  const { status: accountStatus } = useAccount();
  const isBillingEnabled = useBillingFeatureFlag();
  const [userForReactivateBillingFlow, setUserForReactivateBillingFlow] =
    useState<User>();
  const [reactivateStatus, setReactivateStatus] = useState<
    Dictionary<ReactivateStatus>
  >({});
  const availableLicenses = useAvailableLicensesQuery();
  const updateUserStatus = useCallback(
    (userId: number, updatedUser: Partial<User>) => {
      queryClient.setQueryData<MembersResponse>(
        ALL_MEMBERS_QUERY_KEY,
        (previous) => {
          return {
            ...previous,
            members:
              previous?.members.map((member) => {
                return {
                  ...member,
                  user:
                    member.user.userId === userId
                      ? { ...member.user, ...updatedUser }
                      : member.user,
                };
              }) ?? [],
            users: (previous?.users ?? []).map((x) =>
              x.userId === userId ? { ...x, updatedUser } : x,
            ),
          };
        },
      );
    },
    [queryClient],
  );
  const deactivateUser = useCallback(
    (user: User) => {
      updateUserStatus(user.userId, { userStatus: UserStatus.Inactive });
      UserAPI.deactivateUser(user.userId)
        .then(async () => {
          toastService({
            title: translate('success'),
            message: translate('settings.team_members.deactivation.success'),
          }).success();
          await queryClient.invalidateQueries(ALL_PARTNER_COMPANIES_KEY);
          await queryClient.invalidateQueries(ACCOUNT_META_DATA_QUERY_KEY);
          if (subscription?.subscriptionId != null) {
            await forceSyncSubscription(subscription.subscriptionId);
          }
          await queryClient.invalidateQueries(SUBSCRIPTION_KEY);
        })
        .catch(() => {
          toastService({
            title: translate('failure'),
            message: translate('settings.team_members.deactivation.error'),
          }).error();
          updateUserStatus(user.userId, { userStatus: UserStatus.Active });
        });
    },
    [
      updateUserStatus,
      translate,
      queryClient,
      forceSyncSubscription,
      subscription?.subscriptionId,
    ],
  );
  const reactivateUser = useCallback(
    (user: User) => {
      updateUserStatus(user.userId, { userStatus: UserStatus.Active });
      setReactivateStatus((previous) => ({
        ...previous,
        [user.userId]: ReactivateStatus.Reactivating,
      }));

      (isBillingEnabled
        ? BillingAPI.reactivatePartnerMember(user.userId)
        : UserAPI.reactivateUser(user.userId)
      )
        .then(async () => {
          toastService({
            title: translate('success'),
            message: translate('settings.team_members.reactivation.success'),
          }).success();
          await queryClient.invalidateQueries(ALL_PARTNER_COMPANIES_KEY);
          await queryClient.invalidateQueries(ACCOUNT_META_DATA_QUERY_KEY);
          if (subscription?.subscriptionId != null) {
            await forceSyncSubscription(subscription.subscriptionId);
          }
          await queryClient.invalidateQueries(SUBSCRIPTION_KEY);
          setReactivateStatus((previous) => ({
            ...previous,
            [user.userId]: ReactivateStatus.Reactivated,
          }));
        })
        .catch(() => {
          toastService({
            title: translate('failure'),
            message: translate('settings.team_members.reactivation.error'),
          }).error();
          updateUserStatus(user.userId, { userStatus: UserStatus.Inactive });
          setReactivateStatus((previous) => ({
            ...previous,
            [user.userId]: ReactivateStatus.Failed,
          }));
        });
    },
    [
      updateUserStatus,
      isBillingEnabled,
      translate,
      queryClient,
      subscription?.subscriptionId,
      forceSyncSubscription,
    ],
  );
  const confirmDeactivate = useCallback(
    (user: User) => {
      confirm({
        title: translate('settings.team_members.confirmation.title'),
        content: (
          <>
            {translate('settings.team_members.confirmation.text', {
              userName: <strong>{fullName(user)}</strong>,
            })}
            <br />
            <br />
            {translate('settings.team_members.confirmation.description', {
              userName: <strong>{fullName(user)}</strong>,
            })}
          </>
        ),
        okText: translate('settings.team_members.confirmation.cta.submit'),
        onOk() {
          deactivateUser(user);
        },
      });
    },
    [deactivateUser, translate],
  );

  const beforeReactivateUser = useCallback(
    (user: User) => {
      if (
        isBillingEnabled &&
        accountStatus !== AccountStatus.Trial &&
        availableLicenses <= 0
      ) {
        setUserForReactivateBillingFlow(user);
      } else {
        reactivateUser(user);
      }
    },
    [accountStatus, availableLicenses, isBillingEnabled, reactivateUser],
  );
  const onBillingFlowClose = useCallback(() => {
    if (userForReactivateBillingFlow != null) {
      setReactivateStatus((previous) => ({
        ...previous,
        [userForReactivateBillingFlow.userId]: undefined,
      }));
      setUserForReactivateBillingFlow(undefined);
    }
  }, [userForReactivateBillingFlow]);

  return {
    deactivateUser: confirmDeactivate,
    reactivateUser,
    beforeReactivateUser,
    userForReactivatingBillingFlow: userForReactivateBillingFlow,
    reactivateStatus,
    onBillingFlowClose,
  };
};
